import { Building2, Check, Filter, KeyRound, MoreHorizontal, Plus, ShieldCheck, UserRoundCheck, Users } from "lucide-react";
import { useState } from "react";
import { useApp } from "../../domain/AppContext";
import type { Teacher } from "../../domain/types";
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  InlineMessage,
  PageHeader,
  SearchField,
  StatusChip,
} from "../../components/ui";

const permissionGroups = [
  {
    title: "Inscrições e pagamentos",
    description: "Acompanhar inscrições e aprovar cobranças.",
    items: [
      { label: "Visualizar inscrições", ids: ["enrollments:view"], hint: "Permissão por módulo" },
      { label: "Inscrever estudantes em olimpíadas", ids: ["enrollments:create"], hint: "Permissão por módulo" },
      { label: "Registrar pagamento e aprovar inscrições pagas", ids: ["payments:manage"], hint: "Permissão por módulo" },
    ],
  },
  {
    title: "Olimpíadas e resultados",
    description: "Gerenciar competições e seus resultados.",
    items: [
      { label: "Gerenciar olimpíadas", ids: ["olympiads:manage"], hint: "Criar, editar e publicar, sem excluir" },
      { label: "Lançar resultados", ids: ["results:manage"], hint: "Permissão por módulo" },
    ],
  },
  {
    title: "Pessoas e relatórios",
    description: "Acessar dados operacionais da unidade.",
    items: [
      { label: "Editar dados de estudantes", ids: ["students:manage"], hint: "Permissão por módulo" },
      { label: "Redefinir senha de estudantes", ids: ["students:password"], hint: "Permissão por módulo" },
      { label: "Acessar relatórios", ids: ["reports:view"], hint: "Permissão por módulo" },
    ],
  },
] as const;

const coordinatorPermissions = ['enrollments:view', 'enrollments:create', 'payments:manage', 'olympiads:manage', 'results:manage', 'reports:view', 'credentials:view', 'students:manage'];

const permissionAliases: Record<string, readonly string[]> = {
  'olympiads:manage': ['olympiads:manage', 'olympiads:create', 'olympiads:publish'],
};

const expandedPermissionIds = (ids: readonly string[]) => ids.flatMap((id) => permissionAliases[id] ?? [id]);

const hasVisiblePermission = (permissions: string[], ids: readonly string[]) =>
  permissions.includes("*") || expandedPermissionIds(ids).some((id) => permissions.includes(id));

const countVisiblePermissions = (permissions: string[]) =>
  permissionGroups.reduce(
    (total, group) => total + group.items.filter((item) => hasVisiblePermission(permissions, item.ids)).length,
    0,
  );

export function TeachersPage() {
  const { profile, data, updateTeacherPermissions, createTeacher, setTeacherStatus, resetTeacherPassword, notify } = useApp();
  const [query, setQuery] = useState("");
  const [areaFilter, setAreaFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [newTeacher, setNewTeacher] = useState<{ name: string; email: string; areas: string[]; roleLabel: string; permissions: string[]; unitIds: string[]; status: 'active'; isSystemManager?: boolean }>({ name: '', email: '', areas: [], roleLabel: 'Coordenador', permissions: coordinatorPermissions, unitIds: [], status: 'active', isSystemManager: false });
  const [active, setActive] = useState<Teacher | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [working, setWorking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"reset" | "status" | null>(null);
  if (!data) return null;
  const activeTeachers = data.teachers.filter((item) => item.status === "active").length;
  const coordinatorCount = data.teachers.filter((item) => item.roleLabel === "Coordenador").length;
  const managerCount = data.teachers.filter((item) => item.isSystemManager).length;
  const linkedUnitCount = new Set(data.teachers.flatMap((item) => item.unitIds ?? [])).size;
  const filtered = data.teachers.filter((item) =>
    (areaFilter === 'all' || item.areas.includes(areaFilter)) &&
    (roleFilter === 'all' || item.roleLabel === roleFilter) &&
    `${item.name} ${item.email} ${item.areas.join(" ")}`
      .toLocaleLowerCase("pt-BR")
      .includes(query.toLocaleLowerCase("pt-BR")),
  );
  const open = (teacher: Teacher) => {
    setActive(teacher);
    setPermissions([...teacher.permissions]);
    setUnitIds([...(teacher.unitIds ?? [])]);
    setTemporaryPassword("");
    setConfirmAction(null);
  };
  const toggle = (ids: readonly string[]) =>
    setPermissions((items) => {
      const aliases = expandedPermissionIds(ids);
      const enabled = aliases.some((id) => items.includes(id));
      if (enabled) return items.filter((item) => !aliases.includes(item));
      return [...new Set([...items, ...ids])];
    });
  const save = async () => {
    if (!active) return;
    if (!unitIds.length) return notify({ type: "error", title: "Vincule ao menos uma unidade" });
    const result = await updateTeacherPermissions(active.id, permissions, unitIds);
    if (result.ok) {
      notify({
        type: "success",
        title: "Permissões atualizadas",
        description:
          "A alteração foi registrada com segurança.",
      });
      setActive(null);
    } else notify({ type: "error", title: result.error.message });
  };
  const addTeacher = async () => {
    if (!newTeacher.name.trim()) return notify({ type: 'error', title: 'Informe o nome completo' });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newTeacher.email.trim())) return notify({ type: 'error', title: 'Informe um e-mail institucional válido' });
    if (!newTeacher.isSystemManager && !newTeacher.unitIds.length) return notify({ type: 'error', title: 'Vincule ao menos uma unidade' });
    setCreating(true);
    const result = await createTeacher({ ...newTeacher, name: newTeacher.name.trim(), email: newTeacher.email.trim() });
    setCreating(false);
    if (!result.ok) return notify({ type: 'error', title: 'Professor não cadastrado', description: result.error.message });
    setNewOpen(false);
    setNewTeacher({ name: '', email: '', areas: [], roleLabel: 'Coordenador', permissions: coordinatorPermissions, unitIds: profile?.unitId ? [profile.unitId] : [], status: 'active', isSystemManager: false });
    notify({ type: 'success', title: 'Professor cadastrado', description: 'A conta já está ativa e exige a troca da senha inicial no primeiro acesso.' });
  };
  const runSecurityAction = async () => {
    if (!active || !confirmAction) return;
    setWorking(true);
    if (confirmAction === "reset") {
      const result = await resetTeacherPassword(active.id);
      setWorking(false);
      setConfirmAction(null);
      if (result.ok) setTemporaryPassword(result.data.temporaryPassword);
      else notify({ type: "error", title: "Senha não redefinida", description: result.error.message });
      return;
    }
    const result = await setTeacherStatus(active.id, active.status === "active" ? "inactive" : "active");
    setWorking(false);
    setConfirmAction(null);
    if (result.ok) {
      setActive((current) => current ? { ...current, status: result.data.status } : current);
      notify({ type: "success", title: result.data.status === "active" ? "Acesso reativado" : "Acesso desativado" });
    } else notify({ type: "error", title: result.error.message });
  };
  return (
    <div className="teachers-page">
      <PageHeader
        eyebrow="Equipe"
        title="Professores e permissões"
        description="Organize quem pode operar cada parte da plataforma, sempre respeitando sua unidade."
        actions={
          <Button onClick={() => { setNewTeacher((value) => ({ ...value, unitIds: profile?.unitId ? [profile.unitId] : [] })); setNewOpen(true); }}>
            <Plus size={17} /> Novo professor
          </Button>
        }
      />
      <section className="teachers-summary" aria-label="Resumo da equipe">
        <article><span><Users size={19} /></span><div><strong>{data.teachers.length}</strong><small>professores cadastrados</small></div></article>
        <article><span><UserRoundCheck size={19} /></span><div><strong>{activeTeachers}</strong><small>acessos ativos</small></div></article>
        <article><span><ShieldCheck size={19} /></span><div><strong>{coordinatorCount}</strong><small>coordenadores</small></div></article>
        <article><span><Building2 size={19} /></span><div><strong>{linkedUnitCount || data.institution.units.length}</strong><small>unidades em uso</small></div></article>
      </section>
      <section className="teachers-toolbar" aria-label="Buscar e filtrar professores">
        <div className="teachers-toolbar-heading">
          <div><strong>Encontrar professor</strong><span>{filtered.length} de {data.teachers.length} exibido(s)</span></div>
          {query || areaFilter !== "all" || roleFilter !== "all" ? <Button variant="ghost" onClick={() => { setQuery(""); setAreaFilter("all"); setRoleFilter("all"); }}><Filter size={15} /> Limpar filtros</Button> : null}
        </div>
        <div className="admin-filter-bar">
          <SearchField value={query} onChange={setQuery} placeholder="Nome ou e-mail" label="Buscar professor por nome ou e-mail" />
          <select aria-label="Filtrar por área" value={areaFilter} onChange={(event) => setAreaFilter(event.target.value)}>
            <option value="all">Todas as áreas</option>
            {[...new Set(data.teachers.flatMap((item) => item.areas))].sort().map((item) => <option key={item}>{item}</option>)}
          </select>
          <select aria-label="Filtrar por função" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
            <option value="all">Todas as funções</option>
            {[...new Set(data.teachers.map((item) => item.roleLabel))].sort().map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
      </section>
      {filtered.length ? (
        <div className="responsive-table">
          <table>
            <caption className="sr-only">Professores cadastrados e seus níveis de acesso</caption>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Áreas</th>
                <th>Função</th>
                <th>Permissões</th>
                <th>Último acesso</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((teacher) => (
                <tr key={teacher.id}>
                  <td data-label="Nome">
                    <div className="person-cell">
                      <span className="avatar">{teacher.initials}</span>
                      <div><strong>{teacher.name}</strong><small>{teacher.email}</small></div>
                    </div>
                  </td>
                  <td data-label="Áreas">{teacher.areas.length ? teacher.areas.join(", ") : "Não informada"}</td>
                  <td data-label="Função"><span className={`teacher-role ${teacher.isSystemManager ? "teacher-role-manager" : "teacher-role-coordinator"}`}>{teacher.roleLabel}</span></td>
                  <td data-label="Permissões">
                    <span className="teacher-permissions-summary">{teacher.permissions.includes("*") ? "Acesso completo" : `${countVisiblePermissions(teacher.permissions)} ${countVisiblePermissions(teacher.permissions) === 1 ? "poder ativo" : "poderes ativos"}`}</span>
                  </td>
                  <td data-label="Último acesso">{teacher.lastAccess}</td>
                  <td data-label="Status">
                    <StatusChip status={teacher.status} />
                  </td>
                  <td data-label="Ações">
                    <Button variant="ghost" className="teacher-action" disabled={teacher.isSystemManager} title={teacher.isSystemManager ? "Gestores têm acesso total protegido por função" : `Gerenciar acesso de ${teacher.name}`} onClick={() => open(teacher)}>{teacher.isSystemManager ? <><ShieldCheck size={14} /> Acesso total</> : <><MoreHorizontal size={14} /> Gerenciar</>}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<Users />}
          title="Nenhum professor encontrado"
          description="Tente outro termo de busca."
        />
      )}
      <section className="permission-models">
        <div className="section-heading"><div><p className="eyebrow">Referência rápida</p><h2>Modelos de permissão</h2></div><span>{managerCount + coordinatorCount}</span></div>
        <div>
          <article>
            <span className="permission-model-icon"><UserRoundCheck size={17} /></span>
            <div><strong>Coordenador</strong><p>Opera a própria unidade e visualiza Credenciais sem poder editá-las.</p><small>{coordinatorCount} {coordinatorCount === 1 ? "perfil" : "perfis"} no recorte atual</small></div>
          </article>
          <article>
            <span className="permission-model-icon manager"><ShieldCheck size={17} /></span>
            <div><strong>Gestor de Sistema</strong><p>Acesso global, incluindo visualização e edição de Credenciais.</p><small>{managerCount} {managerCount === 1 ? "perfil" : "perfis"} no recorte atual</small></div>
          </article>
        </div>
      </section>
      <Dialog
        open={Boolean(active)}
        title={active ? `Permissões de ${active.name}` : "Permissões"}
        description={
          active
            ? `${active.roleLabel}${active.areas.length ? ` · ${active.areas.join(", ")}` : ""}`
            : undefined
        }
        onClose={() => { setActive(null); setTemporaryPassword(""); setConfirmAction(null); }}
        size="large"
        actions={
          <>
            <Button variant="ghost" onClick={() => { setActive(null); setTemporaryPassword(""); setConfirmAction(null); }}>
              Cancelar
            </Button>
            <Button onClick={save}>Salvar permissões</Button>
          </>
        }
      >
        <div className="permissions-panel">
          <div className="permissions-intro"><div><span className="avatar">{active?.initials}</span></div><div><strong>{active?.roleLabel}</strong><p>Defina unidades e poderes para este acesso.</p></div><span className="permissions-count">{permissions.includes("*") ? "Acesso total" : `${countVisiblePermissions(permissions)} ativos`}</span></div>
          <fieldset className="unit-permissions"><legend>Unidades vinculadas</legend><p className="fieldset-hint">O coordenador só visualiza dados das unidades selecionadas.</p><div className="unit-permissions-options">{data.institution.units.map((unit) => <label key={unit.id}><input type="checkbox" checked={unitIds.includes(unit.id)} onChange={(event) => setUnitIds((items) => event.target.checked ? [...items, unit.id] : items.filter((id) => id !== unit.id))} />{unit.name}</label>)}</div></fieldset>
          <div className="permission-template">
            <ShieldCheck size={20} />
            <div>
              <strong>Aplicar modelo Coordenador</strong>
              <p>Acesso operacional da própria unidade, com Credenciais somente para visualização.</p>
            </div>
            <Button
              variant="secondary"
              onClick={() =>
                setPermissions(coordinatorPermissions)
              }
            >
              Aplicar
            </Button>
          </div>
          <div className="permission-groups">
            {permissionGroups.map((group) => <section key={group.title}><div className="permission-group-heading"><div><strong>{group.title}</strong><small>{group.description}</small></div><span>{group.items.filter((item) => hasVisiblePermission(permissions, item.ids)).length}/{group.items.length}</span></div>{group.items.map((item) => <label key={item.label}><span><strong>{item.label}</strong><small>{item.hint}</small></span><input aria-label={item.label} type="checkbox" checked={hasVisiblePermission(permissions, item.ids)} disabled={permissions.includes("*")} onChange={() => toggle(item.ids)} /></label>)}</section>)}
          </div>
          {confirmAction ? (
            <section className="security-confirmation" role="alert" aria-live="polite">
              <div><strong>{confirmAction === "reset" ? "Redefinir senha do professor?" : active?.status === "active" ? "Desativar este acesso?" : "Reativar este acesso?"}</strong><p>{confirmAction === "reset" ? "Uma senha temporária será gerada e a atual deixará de funcionar." : active?.status === "active" ? "O professor não conseguirá entrar até que o acesso seja reativado." : "O professor voltará a acessar a plataforma com os poderes configurados."}</p></div>
              <div><Button variant="ghost" disabled={working} onClick={() => setConfirmAction(null)}>Cancelar</Button><Button variant={confirmAction === "status" && active?.status === "active" ? "danger" : "primary"} loading={working} onClick={() => void runSecurityAction()}>{confirmAction === "reset" ? "Gerar senha" : active?.status === "active" ? "Desativar" : "Reativar"}</Button></div>
            </section>
          ) : (
            <div className="security-actions">
              <Button variant="secondary" disabled={working} onClick={() => setConfirmAction("reset")}><KeyRound size={15} /> Redefinir senha</Button>
              <Button variant={active?.status === "active" ? "danger" : "secondary"} disabled={working} onClick={() => setConfirmAction("status")}>{active?.status === "active" ? "Desativar acesso" : "Reativar acesso"}</Button>
            </div>
          )}
          {temporaryPassword ? <div className="temporary-password"><InlineMessage type="success" title="Senha temporária gerada">Exibição única. O professor deverá alterá-la no próximo acesso.</InlineMessage><code>{temporaryPassword}</code><Button variant="secondary" onClick={async () => { await navigator.clipboard.writeText(temporaryPassword); notify({ type: "success", title: "Senha temporária copiada" }); }}>Copiar</Button></div> : null}
        </div>
      </Dialog>
      <Dialog open={newOpen} title="Novo professor" description="Cadastre o perfil e defina a hierarquia inicial de acesso." onClose={() => { if (!creating) setNewOpen(false); }} actions={<><Button variant="ghost" disabled={creating} onClick={() => setNewOpen(false)}>Cancelar</Button><Button loading={creating} onClick={() => void addTeacher()}>Cadastrar professor</Button></>}>
        <div className="dialog-form">
          <div className="form-section-intro"><span className="permission-model-icon"><Users size={17} /></span><div><strong>Dados do acesso</strong><p>A conta receberá uma senha inicial e deverá trocá-la no primeiro acesso.</p></div></div>
          <div className="form-grid two"><Field label="Nome completo"><input required value={newTeacher.name} onChange={(event) => setNewTeacher((value) => ({ ...value, name: event.target.value }))} /></Field><Field label="E-mail institucional"><input required type="email" value={newTeacher.email} onChange={(event) => setNewTeacher((value) => ({ ...value, email: event.target.value }))} /></Field></div>
          <div className="form-grid two"><Field label="Função"><select value={newTeacher.roleLabel} onChange={(event) => { const isManager = event.target.value === 'Gestor de Sistema'; setNewTeacher((value) => ({ ...value, roleLabel: event.target.value, isSystemManager: isManager, permissions: isManager ? ['*'] : coordinatorPermissions, unitIds: isManager ? [] : (profile?.unitId ? [profile.unitId] : value.unitIds) })); }}><option>Coordenador</option><option>Gestor de Sistema</option></select></Field><Field label="Área principal"><input value={newTeacher.areas[0] ?? ''} onChange={(event) => setNewTeacher((value) => ({ ...value, areas: [event.target.value] }))} placeholder="Ex.: Olimpíadas" /></Field></div>
          {newTeacher.isSystemManager ? <InlineMessage title="Acesso global">O Gestor de Sistema terá acesso total a todas as unidades e módulos. Para limitar poderes, cadastre o usuário como Coordenador.</InlineMessage> : <fieldset className="unit-permissions"><legend>Unidades vinculadas</legend><p className="fieldset-hint">Selecione ao menos uma unidade para este coordenador.</p><div className="unit-permissions-options">{data.institution.units.map((unit) => <label key={unit.id}><input type="checkbox" checked={newTeacher.unitIds.includes(unit.id)} onChange={(event) => setNewTeacher((value) => ({ ...value, unitIds: event.target.checked ? [...value.unitIds, unit.id] : value.unitIds.filter((id) => id !== unit.id) }))} />{unit.name}</label>)}</div></fieldset>}
          <InlineMessage title="Modelo aplicado">{newTeacher.isSystemManager ? 'Acesso total a unidades, dados, usuários e auditoria.' : 'Coordenador inicia com inscrições, pagamentos, olimpíadas, resultados, relatórios, credenciais e dados de estudantes da própria unidade.'}</InlineMessage>
        </div>
      </Dialog>
    </div>
  );
}
